import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ChargeMapRow } from '../utils/chargeMap';
import { CHARGE_MAP_COLUMNS } from '../lib/comparisonSections';
import { loadNameAliases, type NameAliases, type NameKind } from '../utils/nameAliases';

type ApiResponse<T> = { success: boolean; data?: T; error?: string };

// The select list is shared with the server's report context.
const COLUMNS = CHARGE_MAP_COLUMNS;

// One read per session: ~150 small reference rows that change only when an
// admin adds a spelling. A failed load resolves to an empty lookup (plain
// spelling matching) and is dropped so the next caller retries.
const aliasCache = new Map<NameKind, Promise<NameAliases>>();

const cachedAliases = (kind: NameKind): Promise<NameAliases> => {
  let hit = aliasCache.get(kind);
  if (!hit) {
    hit = loadNameAliases(supabase, kind).then((a) => {
      if (a.size === 0) aliasCache.delete(kind);
      return a;
    });
    aliasCache.set(kind, hit);
  }
  return hit;
};

export const chargeMapService = {
  /**
   * Every mapping row for one retailer - all its rows sharing an approved name
   * (`ResolveResult.group`), so "AGL" finds the rows loaded under "AGL Energy".
   * Fetched whole and resolved in memory: the largest retailer has 151 rows,
   * so filtering per line in SQL would be many round trips for no gain, and
   * the resolver is pure and sync by design.
   */
  forRetailer: async (retailerIds: string[]): Promise<ApiResponse<ChargeMapRow[]>> => {
    try {
      if (retailerIds.length === 0) return { success: true, data: [] };
      const { data, error } = await supabase
        .from('bv_retailer_charge_map')
        .select(COLUMNS)
        .in('retailer_id', retailerIds);
      if (error) throw error;
      return { success: true, data: (data ?? []) as unknown as ChargeMapRow[] };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Approved distributor names and their other spellings, for map resolution. */
  distributorAliases: (): Promise<NameAliases> => cachedAliases('distributor'),

  /** Approved retailer names and their other spellings, for retailer resolution. */
  retailerAliases: (): Promise<NameAliases> => cachedAliases('retailer'),

  /** Drops the cached aliases, after the admin screen edits them. */
  clearAliasCache: (): void => { aliasCache.clear(); },
};
